import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import type { StripeConnectStatus, StripeConnectTariff } from '../../api/types';
import { Alert, Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { PaymentHistoryPanel } from './PaymentHistoryPanel';
import './StripeConnectPanel.css';

interface Props { licenseId: number; isDemo: boolean }

const DEMO_TARIFF: StripeConnectTariff = {
  version: 'es-2026-09-02-v1', status: 'active', acceptanceEnabled: true, hash: 'demo', publishedAt: '2026-09-02T00:00:00.000Z',
  effectiveFrom: '2026-09-02T00:00:00.000Z', currency: 'eur',
  hosty: { bps: 50, appliesTo: 'consumption', appliesToTips: false, vatIncluded: true, vatRateBps: 2100 },
  stripeReference: {
    checkedAt: '2026-09-02', pricingUrl: 'https://stripe.com/es/pricing',
    terminal: { eeaBps: 140, nonEeaBps: 290, fixedCents: 10, tapToPayAuthorizationCents: 10 },
    online: { standardEeaBps: 150, premiumEeaBps: 280, ukBps: 250, internationalBps: 315,
      fixedCents: 25, currencyConversionBps: 200 },
  },
  dinerSurcharge: false,
  refunds: { hostyFee: 'proportional', stripeProcessingFeesRefunded: false, stripeProcessingFeesBorneBy: 'restaurant' },
  disputes: { stripeCostsBorneBy: 'restaurant', hostyMarkupBps: 0 },
  payouts: { standardIncluded: true, instantEnabled: false },
  commercialDecisionsPending: [],
  disclosure: 'Stripe cobra al establecimiento su tarifa aplicable sobre el importe completo, incluida la propina. Hosty cobra el 0,5 % IVA incluido solo sobre el consumo. En una devolución se reintegra proporcionalmente la comisión Hosty; Stripe no devuelve sus costes originales de procesamiento. Los costes reales de disputas corresponden al establecimiento, sin recargo Hosty. La liquidación estándar está incluida y la inmediata está desactivada.',
  accepted: false, acceptedAt: null,
  combinedReference: { terminalEeaBps: 190, terminalNonEeaBps: 340, onlineStandardEeaBps: 200,
    onlinePremiumEeaBps: 330, onlineUkBps: 300, onlineInternationalBps: 365 },
};

export function StripeConnectPanel({ licenseId, isDemo }: Props) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [termsChecked, setTermsChecked] = useState(false);

  const load = useCallback(async () => {
    if (isDemo) {
      setStatus({ connected: false, detailsSubmitted: false, chargesEnabled: false, payoutsEnabled: false,
        feePercent: 0.5, model: null, dashboardAvailable: false, tariff: DEMO_TARIFF });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setStatus(await api.get<StripeConnectStatus>(`/api/license/${licenseId}/connect/status`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo consultar la cuenta de cobros');
    } finally { setLoading(false); }
  }, [isDemo, licenseId]);

  useEffect(() => { void load(); }, [load]);

  async function openStripeLink(kind: 'onboarding-link' | 'dashboard-link') {
    if (isDemo || busy) return;
    const stripeWindow = window.open('', '_blank');
    if (!stripeWindow) {
      setError('El navegador bloqueó la nueva pestaña de Stripe. Permite las ventanas emergentes e inténtalo de nuevo.');
      return;
    }
    stripeWindow.opener = null;
    setBusy(true);
    setError('');
    try {
      const result = await api.post<{ url: string }>(`/api/license/${licenseId}/connect/${kind}`, {});
      stripeWindow.location.replace(result.url);
    } catch (err) {
      stripeWindow.close();
      setError(err instanceof Error ? err.message : 'No se pudo abrir Stripe');
    } finally { setBusy(false); }
  }

  async function acceptTariff() {
    const tariff = status?.tariff;
    if (isDemo || busy || !termsChecked || !tariff) return;
    setBusy(true);
    setError('');
    try {
      const accepted = await api.post<StripeConnectTariff>(
        `/api/license/${licenseId}/connect/tariff-acceptance`,
        { version: tariff.version, hash: tariff.hash },
      );
      setStatus((current) => current ? { ...current, tariff: accepted } : current);
      setTermsChecked(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo registrar la aceptación');
    } finally { setBusy(false); }
  }

  const ready = Boolean(status?.chargesEnabled && status?.payoutsEnabled);
  const direct = status?.model === 'direct';
  const tariff = status?.tariff;
  const tariffAccepted = Boolean(tariff?.accepted);
  const percent = (bps: number) => (bps / 100).toLocaleString('es-ES', { maximumFractionDigits: 2 });
  const cents = (value: number) => (value / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
  const fee = percent(tariff?.hosty.bps ?? Math.round((status?.feePercent ?? 0.5) * 100));

  return (
    <div className="stripe-connect-panel" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border, #e5e7eb)' }}>
      <div className="license-card__meta" style={{ marginBottom: '0.5rem' }}>
        <h4 style={{ margin: 0 }}>Cobros integrados</h4>
        <Badge tone={ready && tariffAccepted ? 'success' : status?.connected ? 'warning' : 'neutral'}>
          {ready && tariffAccepted ? 'Activo' : ready ? 'Tarifa pendiente' : status?.connected ? 'Verificación pendiente' : 'Sin configurar'}
        </Badge>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Stripe procesa los cobros y los abona según el calendario de liquidaciones de la cuenta.
        El componente Hosty configurado es del {fee} % IVA incluido sobre el consumo y no se aplica a las propinas.
        Stripe cobra además el procesamiento sobre el importe completo, incluida la propina.
      </p>
      {tariff ? (
        <section className="payment-tariff" aria-label="Tarifa de cobros">
          <div className="payment-tariff__heading">
            <strong>Referencia estándar actual</strong>
            <span>Versión {tariff.version}</span>
          </div>
          <dl className="payment-tariff__prices">
            <div>
              <dt>Datáfono<span>Tarjeta EEE</span></dt>
              <dd>{percent(tariff.combinedReference.terminalEeaBps)} % + {cents(tariff.stripeReference.terminal.fixedCents)}</dd>
            </div>
            <div>
              <dt>Carta online<span>Tarjeta EEE estándar</span></dt>
              <dd>{percent(tariff.combinedReference.onlineStandardEeaBps)} % + {cents(tariff.stripeReference.online.fixedCents)}</dd>
            </div>
          </dl>
          <details>
            <summary>Ver otras tarjetas y condiciones</summary>
            <ul>
              <li>Datáfono fuera del EEE: {percent(tariff.combinedReference.terminalNonEeaBps)} % + {cents(tariff.stripeReference.terminal.fixedCents)}.</li>
              <li>Tap to Pay: añade {cents(tariff.stripeReference.terminal.tapToPayAuthorizationCents)} por autorización.</li>
              <li>Carta online: EEE prémium {percent(tariff.combinedReference.onlinePremiumEeaBps)} %, Reino Unido {percent(tariff.combinedReference.onlineUkBps)} % e internacional {percent(tariff.combinedReference.onlineInternationalBps)} %; + {cents(tariff.stripeReference.online.fixedCents)}.</li>
              <li>Conversión de divisa: Stripe añade {percent(tariff.stripeReference.online.currencyConversionBps)} % cuando sea necesaria.</li>
              <li>Devoluciones: se reintegra proporcionalmente la comisión Hosty; Stripe no devuelve sus costes originales de procesamiento.</li>
              <li>Disputas: el establecimiento asume el coste real de Stripe, sin recargo Hosty.</li>
              <li>Liquidación estándar incluida; liquidación inmediata desactivada.</li>
            </ul>
            <p>{tariff.disclosure}</p>
            <a href={tariff.stripeReference.pricingUrl} target="_blank" rel="noreferrer">Consultar tarifas oficiales de Stripe</a>
          </details>
          {!tariff.acceptanceEnabled ? (
            <Alert tone="warning">Borrador no contratable. Falta aprobar IVA, devoluciones, disputas y servicios opcionales.</Alert>
          ) : tariff.accepted ? (
            <Alert tone="success">Tarifa aceptada el {new Date(tariff.acceptedAt!).toLocaleString('es-ES')}.</Alert>
          ) : (
            <div className="payment-tariff__acceptance">
              <label>
                <input type="checkbox" checked={termsChecked} disabled={isDemo || busy} onChange={(event) => setTermsChecked(event.target.checked)} />
                <span>He leído la tarifa y acepto el 0,5 % IVA incluido sobre el consumo, la política de devoluciones y que los costes de Stripe y disputas corresponden al establecimiento, sin recargo al comensal.</span>
              </label>
            <Button size="sm" loading={busy} disabled={isDemo || !termsChecked || !tariff.acceptanceEnabled} onClick={() => void acceptTariff()}>Aceptar tarifa</Button>
            </div>
          )}
        </section>
      ) : null}
      {direct ? <p className="muted">Hosty supervisa las disputas y sus plazos, vincula cada caso con la venta y gestiona la evidencia.</p> : null}
      {loading ? <p className="muted">Consultando Stripe…</p> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {!loading && !ready && status?.connected ? <Alert tone="warning">Stripe necesita completar o revisar algunos datos antes de habilitar cobros y liquidaciones.</Alert> : null}

      {!loading ? (
        <div className="license-card__actions">
          {ready && direct ? (
            <Button variant="secondary" size="sm" disabled={isDemo} onClick={() => navigate(`/panel/cobros/${licenseId}`)}>Abrir panel de cobros</Button>
          ) : ready ? (
            <Button variant="secondary" size="sm" loading={busy} disabled={isDemo} onClick={() => void openStripeLink('dashboard-link')}>Abrir panel Express legacy</Button>
          ) : (
            <Button size="sm" loading={busy} disabled={isDemo || !tariffAccepted || !tariff?.acceptanceEnabled} onClick={() => void openStripeLink('onboarding-link')}>{status?.connected ? 'Continuar verificación' : 'Configurar cobros'}</Button>
          )}
          {status?.connected ? <Button variant="ghost" size="sm" disabled={busy} onClick={() => void load()}>Actualizar estado</Button> : null}
        </div>
      ) : null}
      <PaymentHistoryPanel licenseId={licenseId} isDemo={isDemo} />
    </div>
  );
}
